import { useState, useEffect } from "react";
import { ArrowRight, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { getLocalizedInternalUrl } from "@/lib/blog-utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import articleCertificationImg from "@/assets/article-hotel-certification.png";
import articleSustainwizePlatformImg from "@/assets/article-sustainwize-platform.png";
import articleSustainableTrendImg from "@/assets/article-sustainable-hotel-trend.png";

interface BlogArticle {
  title: string;
  excerpt: string;
  url: string;
  image: string;
  imageAlt: string;
  category: string;
  readingTime: number;
}

const SustainwizeArticlesSection = () => {
  const { t, language } = useLanguage();
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('fetch-sustainwize-articles', {
          body: { language }
        });
        
        if (error) {
          console.error('Error fetching sustainwize articles:', error);
          return;
        }
        
        if (data?.success && data?.articles) {
          setArticles(data.articles);
        }
      } catch (error) {
        console.error('Error fetching sustainwize articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [language]);

  // Fallback articles when API fails or returns empty
  const fallbackArticles: BlogArticle[] = [
    {
      title: t("sustainwize.article1Title"),
      excerpt: t("sustainwize.article1Excerpt"),
      category: t("sustainwize.article1Category"),
      url: "/blog/sustainwize-platform-hotel",
      image: articleSustainwizePlatformImg,
      imageAlt: t("sustainwize.article1Title"),
      readingTime: 5
    },
    {
      title: t("sustainwize.article2Title"),
      excerpt: t("sustainwize.article2Excerpt"),
      category: t("sustainwize.article2Category"),
      url: "/blog/sustainable-hotel-trend-2025",
      image: articleSustainableTrendImg,
      imageAlt: t("sustainwize.article2Title"),
      readingTime: 4
    },
    {
      title: t("sustainwize.article3Title"),
      excerpt: t("sustainwize.article3Excerpt"),
      category: t("sustainwize.article3Category"),
      url: "/blog/hotel-third-party-certification",
      image: articleCertificationImg,
      imageAlt: t("sustainwize.article3Title"),
      readingTime: 6
    },
  ];

  const displayArticles = articles.length > 0 ? articles : fallbackArticles;

  // Calculate reading time fallback based on excerpt length if API returns 0
  const getReadingTime = (article: BlogArticle) => {
    if (article.readingTime > 0) return article.readingTime;
    // Fallback: estimate ~1 min per 1000 chars, minimum 1 min
    const excerptLength = article.excerpt?.length || 0;
    return Math.max(1, Math.ceil(excerptLength / 1000));
  };

  return (
    <section className="py-16 lg:py-24 bg-secondary/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-cta text-center">
            {t("sustainwize.articlesTitle")}
          </h2>
          <div className="flex justify-center mt-6 sm:absolute sm:right-0 sm:top-1/2 sm:-translate-y-1/2 sm:mt-0">
            <Link to={language === 'en' ? '/en/blog/tag/sustainability' : '/blog/tag/sustainability'}>
              <Button variant="outline" className="group">
                {t("sustainwize.viewAllArticles")}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card border border-border/50 rounded-2xl overflow-hidden">
                <Skeleton className="h-48 w-full" />
                <div className="p-6 space-y-3">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {displayArticles.map((article, index) => (
                <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <article className="group bg-card border border-border/50 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 h-full">
                    <Link to={getLocalizedInternalUrl(article.url, language)} className="block h-full">
                      <div className="relative h-48 overflow-hidden bg-muted">
                        <img 
                          src={article.image}
                          alt={article.imageAlt || article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          onError={(e) => {
                            const target = e.currentTarget;
                            target.src = "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=400&fit=crop";
                          }}
                        />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                            {article.category}
                          </span>
                        </div>
                      </div>
                      
                      <div className="p-6 flex flex-col h-[calc(100%-12rem)]">
                        <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        
                        <p className="text-muted-foreground text-sm line-clamp-2 mb-4 flex-grow">
                          {article.excerpt}
                        </p>
                        
                        <div className="flex items-center justify-between mt-auto">
                          {getReadingTime(article) > 0 && (
                            <div className="flex items-center text-muted-foreground text-xs">
                              <Clock className="w-3 h-3 mr-1" />
                              <span>{getReadingTime(article)} min read</span>
                            </div>
                          )}
                          
                          <span className="text-primary font-medium text-sm flex items-center gap-1">
                            {t("sustainwize.readMore")}
                            <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </article>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-4" />
            <CarouselNext className="hidden md:flex -right-4" />
          </Carousel>
        )}
      </div>
    </section>
  );
};

export default SustainwizeArticlesSection;
